import React from 'react';
import { Droplet, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useVault } from '../context/VaultContext';
import { usePalmentWallet } from '../context/DemoWalletContext';
import VaultTabs from './vault/VaultTabs';
import VaultAmountInput from './vault/VaultAmountInput';
import VaultPreviewCard from './vault/VaultPreviewCard';
import VaultDisconnectedOverlay from './vault/VaultDisconnectedOverlay';
import VaultSubmitButton from './vault/VaultSubmitButton';
import useVaultForm from './vault/useVaultForm';

export default function VaultPanel() {
  const { connected, startDemo, openRealWallet } = usePalmentWallet();
  const { claimFaucet, hasFaucetClaimed } = useVault();

  const form = useVaultForm();
  const {
    mode,
    amount,
    submitting,
    justSucceeded,
    parsed,
    preview,
    maxAvailable,
    apy,
    exchangeRate,
    pusdBalance,
    validationMessage,
    setAmount,
    changeMode,
    submit,
  } = form;

  const handleDemo = () => {
    startDemo();
    toast.success('Demo mode enabled', {
      description: 'Vault unlocked. Claim faucet PUSD to begin.',
      icon: <Sparkles size={16} />,
    });
  };

  const handleInlineFaucet = async () => {
    try {
      const res = await claimFaucet();
      toast.success('1,000 Mock PUSD minted to your wallet', {
        description: res?.signature
          ? `Devnet memo tx: ${res.signature.slice(0, 8)}…${res.signature.slice(-6)}`
          : undefined,
      });
    } catch (err) {
      console.error('[palment] inline faucet failed', err);
      const msg = (err && (err.message || err.toString())) || 'Unknown error';
      const rejected = /reject|denied|user|declin/i.test(msg);
      toast.error(rejected ? 'Faucet rejected in wallet' : 'Faucet failed', {
        description: rejected ? undefined : msg.slice(0, 160),
      });
    }
  };

  const submitDisabled =
    submitting || (connected && (parsed <= 0 || parsed > maxAvailable));

  return (
    <section id="vault" className="relative py-10 md:py-16">
      {/* Accent gradient under panel */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-64 blur-3xl opacity-40"
        style={{
          background:
            'linear-gradient(135deg, rgba(7,102,83,0.7) 0%, rgba(226,251,206,0.2) 100%)',
        }}
      />

      <div className="relative max-w-4xl mx-auto px-4">
        <VaultSectionHeader apy={apy} />

        <div
          className="relative rounded-3xl glass-strong p-6 md:p-10 overflow-hidden"
          data-testid="vault-panel"
        >
          <div
            aria-hidden
            className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-30 blur-3xl"
            style={{ background: 'radial-gradient(circle, #E3EF26, transparent 65%)' }}
          />

          {!connected && (
            <VaultDisconnectedOverlay onConnect={openRealWallet} onDemo={handleDemo} />
          )}

          <div
            className={`relative ${
              connected
                ? ''
                : 'blur-sm pointer-events-none select-none opacity-70 grayscale-[15%]'
            } transition-all duration-500`}
          >
            <VaultTabs mode={mode} onChange={changeMode} />

            <div className="mt-8 grid md:grid-cols-5 gap-8 items-start">
              <div className="md:col-span-3">
                <VaultAmountInput
                  mode={mode}
                  amount={amount}
                  onAmountChange={setAmount}
                  maxAvailable={maxAvailable}
                />

                {mode === 'deposit' && pusdBalance <= 0 && connected && (
                  <button
                    type="button"
                    onClick={handleInlineFaucet}
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl glass border-palm-lime/30 hover:border-palm-lime/70 text-sm font-semibold transition-all"
                    data-testid="vault-faucet-inline"
                  >
                    <Droplet size={14} strokeWidth={1.8} className="text-palm-lime" />
                    {hasFaucetClaimed
                      ? 'Top up with 1,000 Mock PUSD (devnet)'
                      : 'Need PUSD? Claim 1,000 from the devnet faucet'}
                  </button>
                )}
              </div>

              <div className="md:col-span-2">
                <VaultPreviewCard
                  mode={mode}
                  preview={preview}
                  apy={apy}
                  exchangeRate={exchangeRate}
                />
              </div>
            </div>

            {validationMessage && connected && (
              <div className="mt-5 text-sm text-red-300/90" data-testid="validation-error">
                {validationMessage}
              </div>
            )}

            <VaultSubmitButton
              mode={mode}
              connected={connected}
              submitting={submitting}
              justSucceeded={justSucceeded}
              disabled={submitDisabled}
              onClick={submit}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function VaultSectionHeader({ apy }) {
  return (
    <div className="flex items-end justify-between mb-6 px-2">
      <div>
        <div className="text-[11px] tracking-[0.26em] uppercase text-palm-mint/70">
          Yield Vault
        </div>
        <h2 className="font-display font-bold tracking-tight text-3xl md:text-4xl mt-2">
          Stake <span className="text-gradient">PUSD</span>. Earn forever.
        </h2>
      </div>
      <div className="hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-palm-lime animate-pulseSlow" />
        <span className="text-xs text-palm-mint/80 tracking-widest uppercase">
          Live · {apy}% APY
        </span>
      </div>
    </div>
  );
}
